import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@apollo/react-hooks'
import Select from 'react-select'
import * as Query from '../api/gql/query'

export interface TenderOption {
  value: string
  label: string
}

interface Props {
  projectId: string
  value: TenderOption[]
  onChange: (value: TenderOption[]) => void
  placeholder?: string
  className?: string
}

export const tenderOptionFromNode = (t: any): TenderOption => ({
  value: t.id,
  label: t.name,
})

const TenderLinkPicker: React.FC<Props> = ({
  projectId,
  value,
  onChange,
  placeholder,
  className,
}) => {
  const { t } = useTranslation()

  const { data, loading } = useQuery(Query.TENDERS, {
    variables: { projectId },
    skip: !projectId,
  })

  const options = useMemo<TenderOption[]>(
    () => (data?.tenders || []).map(tenderOptionFromNode),
    [data]
  )

  const ASYNC_CLASSES = {
    container: () => 'w-full',
    control: ({ isFocused }: any) =>
      `flex min-h-9 w-full items-center rounded-md border bg-card pl-2 pr-1 text-sm transition-colors ${
        isFocused
          ? 'border-ring ring-2 ring-ring ring-offset-0'
          : 'border-input'
      }`,
    valueContainer: () => 'flex flex-wrap gap-1 py-1',
    placeholder: () => 'text-muted-foreground',
    singleValue: () => 'text-foreground',
    input: () =>
      'text-foreground m-0 py-0 [&>input]:bg-transparent [&>input]:border-0 [&>input]:outline-none [&>input]:shadow-none [&>input]:focus:ring-0',
    multiValue: () =>
      'inline-flex items-center gap-1 bg-muted rounded px-1.5 py-0.5 text-xs',
    multiValueLabel: () => 'leading-none',
    multiValueRemove: () =>
      'ml-0.5 hover:bg-muted-foreground/20 rounded cursor-pointer flex items-center',
    indicatorsContainer: () => 'flex items-center gap-0.5 shrink-0',
    clearIndicator: () =>
      'p-1 text-muted-foreground hover:text-foreground cursor-pointer',
    indicatorSeparator: () => 'hidden',
    dropdownIndicator: () =>
      'p-1 text-muted-foreground hover:text-foreground cursor-pointer',
    loadingIndicator: () => 'p-1 text-muted-foreground',
    menu: () =>
      'mt-1 overflow-hidden rounded-md border border-border bg-popover shadow-md z-[1200]',
    menuList: () => 'overflow-y-auto py-1',
    option: ({ isFocused, isSelected }: any) =>
      `px-3 py-2 text-sm cursor-pointer ${
        isSelected
          ? 'bg-primary text-primary-foreground'
          : isFocused
            ? 'bg-accent text-accent-foreground'
            : ''
      }`,
    noOptionsMessage: () => 'px-3 py-2 text-sm text-muted-foreground',
    loadingMessage: () => 'px-3 py-2 text-sm text-muted-foreground',
  }

  return (
    <div className={className}>
      <Select
        isMulti
        isClearable
        isSearchable
        options={options}
        isLoading={loading}
        value={value}
        onChange={(opts: any) => onChange((opts as TenderOption[]) || [])}
        placeholder={placeholder || t('TenderLinkPicker_placeholder')}
        noOptionsMessage={() => t('TenderLinkPicker_empty')}
        loadingMessage={() => t('Common_loading')}
        classNames={ASYNC_CLASSES as any}
        menuPlacement="auto"
        maxMenuHeight={280}
        unstyled
        closeMenuOnSelect={false}
        hideSelectedOptions={false}
      />
    </div>
  )
}

export default TenderLinkPicker
